import React, { useState } from "react";
import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  useIonModal
} from "@ionic/react";
import { lockClosedOutline, eyeOutline, eyeOffOutline } from "ionicons/icons";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { decryptString, encryptString } from "../lib/cryptography";
import toast from "react-hot-toast";
import { useUsersStore } from "../stores/users.store";
import HelpButton from "./HelpButton";
import TermsAndConditions from "../pages/Terms";
import axios from "axios";
import getCoinImage from "../utils/getCoinImage";

interface Secrets {
  apiKey: string;
  apiSecret: string;
}

interface Props {
  onSubmit: (binanceSecrets: Secrets, isValid: boolean) => void;
  binanceSecrets?: Secrets;
}
const ExchangeApiForm: React.FC<Props> = ({ onSubmit, binanceSecrets }) => {
  const globalSecrets: any = useUsersStore(state => state.user?.binanceSecrets);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [presentTermsModal, dismissTermsModal] = useIonModal(
    TermsAndConditions,
    {
      dismiss: () => dismissTermsModal()
    }
  );

  const handleFormSubmit = async (values: Secrets, { setSubmitting }: any) => {
    if (!acceptDisclaimer) {
      toast.error("Please accept the disclaimer.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        "https://asia-northeast1-quidpro-903c6.cloudfunctions.net/validate_binance_api_creds",
        {
          apiKey: values.apiKey,
          apiSecret: values.apiSecret
        }
      );

      if (response.data.isValid) {
        // Check USDT balance
        const usdtBalance = response.data.data.balances.find(
          (balance: { asset: string; free: string }) => balance.asset === "USDT"
        );
        if (
          !usdtBalance ||
          (usdtBalance && parseFloat(usdtBalance.free) < 10)
        ) {
          toast.error(
            "API Valid but please acquire some USDT to fund your bot for trading before validation will be accepted.",
            { duration: 5000 }
          );
          setSubmitting(false);
          return;
        }

        // If the balance is sufficient or there is no USDT balance (user might not be trading with USDT), proceed
        onSubmit(
          {
            apiKey: encryptString(values.apiKey),
            apiSecret: encryptString(values.apiSecret)
          },
          true
        );
        toast.success(
          response.data.message || "API credentials updated successfully",
          { duration: 3000 }
        );
      } else {
        // Display error from the response
        toast.error(response.data.error, { duration: 5000 });
      }
    } catch (error) {
      // Handle errors from the API call itself (e.g., network error)
      toast.error(
        "An error occurred while validating API credentials. Please try again.",
        { duration: 5000 }
      );
    }

    setSubmitting(false);
  };

  const secrets = binanceSecrets || globalSecrets;

  // Form validation schema
  const validationSchema = Yup.object().shape({
    apiKey: Yup.string().required("API Key is required"),
    apiSecret: Yup.string().required("API Secret is required")
  });

  return (
    <>
      <div className="flex items-center justify-between pt-2">
        <div className="flex text-xl font-bold px-4">
          <img
            className="h-8 pr-2"
            src={getCoinImage("BNB")}
            alt="Binance Coin"
          />
          Binance API Credentials
        </div>
        <IonButton
          fill="clear"
          size="small"
          rel="noopener noreferrer"
          href="https://www.binance.com/en?ref=19763815"
          target="_blank"
        >
          <span className="text-primary-50">Join Binance</span>
        </IonButton>
        <HelpButton id="live_bot_binance_api" />
      </div>
      <Formik
        initialValues={{
          apiKey: secrets?.apiKey ? decryptString(secrets.apiKey) : "",
          apiSecret: secrets?.apiSecret ? decryptString(secrets.apiSecret) : ""
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ isSubmitting, handleChange, handleBlur, values }) => (
          <Form>
            <IonItem lines="full">
              <IonLabel position="stacked">API Key</IonLabel>
              <IonInput
                name="apiKey"
                type={showApiKey ? "text" : "password"}
                value={values.apiKey}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                placeholder="Enter API Key"
                required
              />
              <IonIcon
                slot="end"
                icon={showApiKey ? eyeOffOutline : eyeOutline}
                onClick={() => setShowApiKey(!showApiKey)}
              />
            </IonItem>

            <IonItem lines="full" className="mt-4">
              <IonLabel position="stacked">API Secret</IonLabel>
              <IonInput
                name="apiSecret"
                type={showApiSecret ? "text" : "password"}
                value={values.apiSecret}
                onIonChange={handleChange}
                onIonBlur={handleBlur}
                placeholder="Enter API Secret"
                required
              />
              <IonIcon
                slot="end"
                icon={showApiSecret ? eyeOffOutline : eyeOutline}
                onClick={() => setShowApiSecret(!showApiSecret)}
              />
            </IonItem>

            <div className="p-4 flex justify-center items-center">
              <IonCheckbox
                checked={acceptDisclaimer}
                onIonChange={e => setAcceptDisclaimer(e.detail.checked)}
              />
              <span className="pl-2">
                I accept the{" "}
                <IonText
                  className="underline text-sm cursor-pointer"
                  onClick={e => {
                    presentTermsModal();
                  }}
                >
                  {"Terms and Conditions"}
                </IonText>
              </span>
            </div>

            <IonButton
              type="submit"
              expand="block"
              color="primary"
              className="mt-6 mx-4"
              disabled={isSubmitting || !acceptDisclaimer}
            >
              Validate API Credentials
              <IonIcon slot="end" icon={lockClosedOutline} />
            </IonButton>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ExchangeApiForm;
